/**
 * TechTreeCanvas - Canvas-based tech tree visualization
 * Style: Game skill tree with icon nodes and arrow connections
 * Uses individual icon files for tech icons
 */

import type { Tech, TechId, TechStatus, TechBranch } from '@/game/tech/TechTypes';

export interface TechNodeInfo {
  tech: Tech;
  status: TechStatus;
  x: number;
  y: number;
  size: number;
  shape: 'circle' | 'square';
}

export interface TechTreeCanvasOptions {
  width: number;
  height: number;
  onTechClick?: (techId: TechId) => void;
  onTechHover?: (tech: Tech | null, x: number, y: number) => void;
}

// Selected tech ID for highlighting
let selectedTechId: TechId | null = null;

// Individual icon images cache
const ICON_CACHE: Map<TechId, HTMLImageElement> = new Map();
const ICON_LOADING: Set<TechId> = new Set();

// Get icon file number from tech ID (e.g., G001 -> 1, G011 -> 4, etc.)
function getTechIconNumber(techId: TechId): number | null {
  const match = techId.match(/^([A-Z])(\d+)$/);
  if (!match) return null;

  const num = parseInt(match[2], 10);

  // Map based on tier structure (3+5+5+3+2 = 18 techs)
  if (num >= 41) return 17 + (num - 41);      // Tier 5: 17-18
  if (num >= 31) return 14 + (num - 31);      // Tier 4: 14-16
  if (num >= 21) return 9 + (num - 21);       // Tier 3: 9-13
  if (num >= 11) return 4 + (num - 11);       // Tier 2: 4-8
  return num;                                  // Tier 1: 1-3
}

// Get branch from tech ID
function getBranchFromTechId(techId: TechId): TechBranch | null {
  if (techId.startsWith('B')) return 'bingfa';
  if (techId.startsWith('G')) return 'baigong';
  if (techId.startsWith('N')) return 'nonggeng';
  if (techId.startsWith('S')) return 'shangdao';
  if (techId.startsWith('M')) return 'moxue';
  if (techId.startsWith('Y')) return 'yinyang';
  if (techId.startsWith('F_')) return 'faction';
  if (techId === 'ULTIMATE') return 'ultimate';
  return null;
}

// Get icon URL for a tech
function getTechIconUrl(techId: TechId): string | null {
  // Ultimate tech
  if (techId === 'ULTIMATE') {
    return '/images/tech/ultimate.webp';
  }

  // Faction techs: F_HAN_001 -> /images/tech/han/H1.webp
  const factionMatch = techId.match(/^F_([A-Z]+)_(\d+)$/);
  if (factionMatch) {
    const [, faction, numStr] = factionMatch;
    const num = parseInt(numStr, 10);
    const factionLower = faction.toLowerCase();
    const prefix = faction.charAt(0);
    return `/images/tech/${factionLower}/${prefix}${num}.webp`;
  }

  // Regular branch techs
  const branch = getBranchFromTechId(techId);
  if (!branch || branch === 'faction' || branch === 'ultimate') return null;

  const iconNum = getTechIconNumber(techId);
  if (iconNum === null) return null;

  return `/images/tech/${branch}/${techId.charAt(0)}${iconNum}.webp`;
}

// Load individual icon for a tech
function loadTechIcon(techId: TechId): void {
  if (ICON_CACHE.has(techId) || ICON_LOADING.has(techId)) return;

  const url = getTechIconUrl(techId);
  if (!url) return;

  ICON_LOADING.add(techId);

  const img = new Image();
  img.onload = () => {
    ICON_CACHE.set(techId, img);
    ICON_LOADING.delete(techId);
  };
  img.onerror = () => {
    ICON_LOADING.delete(techId);
    console.warn(`Failed to load icon for ${techId}`);
  };
  img.src = url;
}

export class TechTreeCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private options: TechTreeCanvasOptions;

  // View state
  private offsetX: number = 0;
  private offsetY: number = 0;
  private scale: number = 1;
  private minScale: number = 0.4;
  private maxScale: number = 2;

  // Interaction state
  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private hoveredTech: Tech | null = null;

  // Tech data
  private techs: Tech[] = [];
  private techStatuses: Map<TechId, TechStatus> = new Map();
  private nodeInfos: TechNodeInfo[] = [];

  // Node dimensions
  private nodeSize: number = 42;
  private nodeSpacing: number = 62;
  private tierSpacing: number = 75;

  // Animation
  private pulsePhase: number = 0;
  private animationFrame: number = 0;

  constructor(container: HTMLElement, options: TechTreeCanvasOptions) {
    this.options = options;

    this.canvas = document.createElement('canvas');
    this.canvas.width = options.width;
    this.canvas.height = options.height;
    this.canvas.style.cursor = 'grab';

    this.ctx = this.canvas.getContext('2d')!;
    container.appendChild(this.canvas);

    this.setupEventListeners();
    this.startAnimation();
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.canvas.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.canvas.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.canvas.addEventListener('wheel', this.onWheel.bind(this));
    this.canvas.addEventListener('click', this.onClick.bind(this));

    this.canvas.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.canvas.addEventListener('touchmove', this.onTouchMove.bind(this));
    this.canvas.addEventListener('touchend', this.onTouchEnd.bind(this));
  }

  private onMouseDown(e: MouseEvent): void {
    this.isDragging = true;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
    this.canvas.style.cursor = 'grabbing';
  }

  private onMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (this.isDragging) {
      const dx = e.clientX - this.lastMouseX;
      const dy = e.clientY - this.lastMouseY;
      this.offsetX += dx;
      this.offsetY += dy;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    } else {
      const worldX = (mouseX - this.offsetX) / this.scale;
      const worldY = (mouseY - this.offsetY) / this.scale;
      const hovered = this.getTechAtPosition(worldX, worldY);

      if (hovered !== this.hoveredTech) {
        this.hoveredTech = hovered;
        this.canvas.style.cursor = hovered ? 'pointer' : 'grab';
        this.options.onTechHover?.(hovered, e.clientX, e.clientY);
      }
    }
  }

  private onMouseUp(): void {
    this.isDragging = false;
    this.canvas.style.cursor = this.hoveredTech ? 'pointer' : 'grab';
  }

  private onMouseLeave(): void {
    this.isDragging = false;
    this.hoveredTech = null;
    this.options.onTechHover?.(null, 0, 0);
    this.canvas.style.cursor = 'grab';
  }

  private onClick(): void {
    if (this.hoveredTech) {
      selectedTechId = this.hoveredTech.id;
      this.options.onTechClick?.(this.hoveredTech.id);
    }
  }

  setSelectedTech(techId: TechId | null): void {
    selectedTechId = techId;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * zoomFactor));

    if (newScale !== this.scale) {
      const worldX = (mouseX - this.offsetX) / this.scale;
      const worldY = (mouseY - this.offsetY) / this.scale;
      this.scale = newScale;
      this.offsetX = mouseX - worldX * this.scale;
      this.offsetY = mouseY - worldY * this.scale;
    }
  }

  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      this.isDragging = true;
      this.lastMouseX = e.touches[0].clientX;
      this.lastMouseY = e.touches[0].clientY;
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 1 && this.isDragging) {
      const dx = e.touches[0].clientX - this.lastMouseX;
      const dy = e.touches[0].clientY - this.lastMouseY;
      this.offsetX += dx;
      this.offsetY += dy;
      this.lastMouseX = e.touches[0].clientX;
      this.lastMouseY = e.touches[0].clientY;
    }
  }

  private onTouchEnd(): void {
    this.isDragging = false;
  }

  private getTechAtPosition(x: number, y: number): Tech | null {
    for (const info of this.nodeInfos) {
      const dx = x - info.x;
      const dy = y - info.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= info.size / 2 + 4) {
        return info.tech;
      }
    }
    return null;
  }

  private startAnimation(): void {
    const animate = () => {
      this.pulsePhase += 0.03;
      this.render();
      this.animationFrame = requestAnimationFrame(animate);
    };
    this.animationFrame = requestAnimationFrame(animate);
  }

  setTechs(techs: Tech[], statuses: Map<TechId, TechStatus>): void {
    this.techs = techs;
    this.techStatuses = statuses;

    // Preload icons for all techs
    for (const tech of techs) {
      loadTechIcon(tech.id);
    }

    this.calculateNodePositions();
    this.centerView();
  }

  private calculateNodePositions(): void {
    this.nodeInfos = [];

    const tierGroups = new Map<number, Tech[]>();
    for (const tech of this.techs) {
      if (!tierGroups.has(tech.tier)) {
        tierGroups.set(tech.tier, []);
      }
      tierGroups.get(tech.tier)!.push(tech);
    }

    const tiers = Array.from(tierGroups.keys()).sort((a, b) => a - b);

    for (const tier of tiers) {
      const tierTechs = tierGroups.get(tier)!;
      const y = 60 + (tier - 1) * this.tierSpacing;

      const totalWidth = tierTechs.length * this.nodeSpacing;
      const startX = -totalWidth / 2 + this.nodeSpacing / 2;

      tierTechs.forEach((tech, index) => {
        let x: number;
        let posY: number;

        if (tech.position) {
          x = (tech.position.x - 50) * 6;
          posY = tech.position.y * 4.5;
        } else {
          x = startX + index * this.nodeSpacing;
          posY = y;
        }

        const shape: 'circle' | 'square' = 'circle';

        this.nodeInfos.push({
          tech,
          status: this.techStatuses.get(tech.id) || 'locked',
          x,
          y: posY,
          size: this.nodeSize,
          shape,
        });
      });
    }
  }

  private centerView(): void {
    if (this.nodeInfos.length === 0) return;

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    for (const info of this.nodeInfos) {
      minX = Math.min(minX, info.x - info.size);
      maxX = Math.max(maxX, info.x + info.size);
      minY = Math.min(minY, info.y - info.size);
      maxY = Math.max(maxY, info.y + info.size);
    }

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const scaleX = (this.canvas.width - 60) / contentWidth;
    const scaleY = (this.canvas.height - 60) / contentHeight;
    this.scale = Math.min(scaleX, scaleY, 1);
    this.scale = Math.max(this.minScale, Math.min(this.maxScale, this.scale));

    this.offsetX = this.canvas.width / 2 - centerX * this.scale;
    this.offsetY = this.canvas.height / 2 - centerY * this.scale;
  }

  zoomIn(): void {
    const newScale = Math.min(this.maxScale, this.scale * 1.2);
    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const worldX = (centerX - this.offsetX) / this.scale;
    const worldY = (centerY - this.offsetY) / this.scale;
    this.scale = newScale;
    this.offsetX = centerX - worldX * this.scale;
    this.offsetY = centerY - worldY * this.scale;
  }

  zoomOut(): void {
    const newScale = Math.max(this.minScale, this.scale / 1.2);
    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const worldX = (centerX - this.offsetX) / this.scale;
    const worldY = (centerY - this.offsetY) / this.scale;
    this.scale = newScale;
    this.offsetX = centerX - worldX * this.scale;
    this.offsetY = centerY - worldY * this.scale;
  }

  resetView(): void {
    this.centerView();
  }

  render(): void {
    const ctx = this.ctx;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    this.drawConnections();

    for (const info of this.nodeInfos) {
      this.drawNode(info);
    }

    ctx.restore();
  }

  private drawConnections(): void {
    for (const info of this.nodeInfos) {
      const tech = info.tech;

      for (const prereqId of tech.prerequisites) {
        const prereqInfo = this.nodeInfos.find(n => n.tech.id === prereqId);
        if (prereqInfo) {
          this.drawArrowConnection(prereqInfo, info, 'normal');
        }
      }

      if (tech.mutuallyExclusive) {
        for (const exclusiveId of tech.mutuallyExclusive) {
          const exclusiveInfo = this.nodeInfos.find(n => n.tech.id === exclusiveId);
          if (exclusiveInfo && tech.id < exclusiveId) {
            this.drawExclusionConnection(info, exclusiveInfo);
          }
        }
      }
    }
  }

  private drawArrowConnection(from: TechNodeInfo, to: TechNodeInfo, type: 'normal' | 'alternative'): void {
    const ctx = this.ctx;

    const fromY = from.y + from.size / 2;
    const toY = to.y - to.size / 2;

    let lineColor = 'rgba(180, 180, 180, 0.6)';
    if (to.status === 'completed' || from.status === 'completed') {
      lineColor = 'rgba(212, 175, 55, 0.7)';
    } else if (to.status === 'available') {
      lineColor = 'rgba(255, 255, 255, 0.8)';
    }

    ctx.strokeStyle = lineColor;
    ctx.fillStyle = lineColor;
    ctx.lineWidth = 2;

    if (type === 'alternative') {
      ctx.setLineDash([4, 4]);
    } else {
      ctx.setLineDash([]);
    }

    ctx.beginPath();

    if (Math.abs(from.x - to.x) < 5) {
      ctx.moveTo(from.x, fromY);
      ctx.lineTo(to.x, toY);
    } else {
      const midY = (fromY + toY) / 2;
      ctx.moveTo(from.x, fromY);
      ctx.lineTo(from.x, midY);
      ctx.lineTo(to.x, midY);
      ctx.lineTo(to.x, toY);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    const arrowSize = 8;
    ctx.beginPath();
    ctx.moveTo(to.x, toY);
    ctx.lineTo(to.x - arrowSize / 2, toY - arrowSize);
    ctx.lineTo(to.x + arrowSize / 2, toY - arrowSize);
    ctx.closePath();
    ctx.fill();
  }

  private drawExclusionConnection(a: TechNodeInfo, b: TechNodeInfo): void {
    const ctx = this.ctx;

    ctx.strokeStyle = 'rgba(198, 40, 40, 0.6)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);

    ctx.beginPath();
    ctx.moveTo(a.x + a.size / 2, a.y);
    ctx.lineTo(b.x - b.size / 2, b.y);
    ctx.stroke();

    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;
    const xSize = 6;

    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(midX - xSize, midY - xSize);
    ctx.lineTo(midX + xSize, midY + xSize);
    ctx.moveTo(midX + xSize, midY - xSize);
    ctx.lineTo(midX - xSize, midY + xSize);
    ctx.stroke();
  }

  private drawNode(info: TechNodeInfo): void {
    const ctx = this.ctx;
    const { tech, status, x, y, size, shape } = info;

    const isHovered = this.hoveredTech?.id === tech.id;
    const isSelected = selectedTechId === tech.id;
    const halfSize = size / 2;

    const colors = this.getNodeColors(status);

    // Draw outer glow
    if (status === 'researching' || status === 'available' || isHovered || isSelected) {
      const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;

      ctx.save();
      if (isSelected) {
        ctx.shadowColor = '#ffd700';
        ctx.shadowBlur = 20;
      } else if (status === 'researching') {
        ctx.shadowColor = '#4fc3f7';
        ctx.shadowBlur = 15 * pulse;
      } else if (status === 'available') {
        ctx.shadowColor = '#81c784';
        ctx.shadowBlur = isHovered ? 15 : 8;
      } else if (isHovered) {
        ctx.shadowColor = colors.border;
        ctx.shadowBlur = 12;
      }

      ctx.beginPath();
      if (shape === 'circle') {
        ctx.arc(x, y, halfSize, 0, Math.PI * 2);
      } else {
        ctx.roundRect(x - halfSize, y - halfSize, size, size, 6);
      }
      ctx.fillStyle = 'transparent';
      ctx.fill();
      ctx.restore();
    }

    // Draw selection ring
    if (isSelected) {
      ctx.beginPath();
      if (shape === 'circle') {
        ctx.arc(x, y, halfSize + 6, 0, Math.PI * 2);
      } else {
        ctx.roundRect(x - halfSize - 6, y - halfSize - 6, size + 12, size + 12, 10);
      }
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw node border
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(x, y, halfSize + 3, 0, Math.PI * 2);
    } else {
      ctx.roundRect(x - halfSize - 3, y - halfSize - 3, size + 6, size + 6, 8);
    }
    ctx.strokeStyle = colors.border;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw node background
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(x, y, halfSize, 0, Math.PI * 2);
    } else {
      ctx.roundRect(x - halfSize, y - halfSize, size, size, 6);
    }

    const gradient = ctx.createRadialGradient(x, y - halfSize / 2, 0, x, y, halfSize * 1.5);
    gradient.addColorStop(0, colors.bgTop);
    gradient.addColorStop(1, colors.bgBottom);
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.strokeStyle = colors.innerBorder;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Draw icon
    this.drawTechIcon(ctx, tech, x, y, size, status);

    // Draw tier badge for high tier techs
    if (tech.tier >= 4) {
      const badgeX = x + halfSize - 8;
      const badgeY = y + halfSize - 8;
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, 10, 0, Math.PI * 2);
      ctx.fillStyle = tech.tier === 5 ? '#d4af37' : '#1565c0';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(tech.tier), badgeX, badgeY);
    }

    // Draw progress bar for researching
    if (status === 'researching') {
      const barWidth = size * 0.8;
      const barHeight = 4;
      const barX = x - barWidth / 2;
      const barY = y + halfSize + 8;
      const progress = 0.5 + Math.sin(this.pulsePhase * 2) * 0.1;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(barX, barY, barWidth, barHeight);
      ctx.fillStyle = '#4fc3f7';
      ctx.fillRect(barX, barY, barWidth * progress, barHeight);
    }
  }

  private drawTechIcon(
    ctx: CanvasRenderingContext2D,
    tech: Tech,
    x: number,
    y: number,
    size: number,
    status: TechStatus
  ): void {
    // Calculate draw size (fit within node)
    const drawSize = size * 0.9;
    const drawX = x - drawSize / 2;
    const drawY = y - drawSize / 2;

    // Check for cached icon
    const icon = ICON_CACHE.get(tech.id);
    if (icon) {
      // Apply transparency for locked/excluded
      if (status === 'locked' || status === 'excluded') {
        ctx.save();
        ctx.globalAlpha = 0.5;
      }

      // Clip to circle/square shape
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, size / 2 - 2, 0, Math.PI * 2);
      ctx.clip();

      ctx.drawImage(icon, drawX, drawY, drawSize, drawSize);

      ctx.restore();

      if (status === 'locked' || status === 'excluded') {
        ctx.restore();
      }
    }
    // Don't show fallback - wait for icon to load
  }

  private getNodeColors(status: TechStatus): {
    border: string;
    innerBorder: string;
    bgTop: string;
    bgBottom: string;
  } {
    switch (status) {
      case 'completed':
        return {
          border: '#d4af37',
          innerBorder: '#ffd700',
          bgTop: '#4a4020',
          bgBottom: '#2a2510',
        };
      case 'researching':
        return {
          border: '#4fc3f7',
          innerBorder: '#81d4fa',
          bgTop: '#1e3a5f',
          bgBottom: '#0d2137',
        };
      case 'available':
        return {
          border: '#81c784',
          innerBorder: '#a5d6a7',
          bgTop: '#2d4a2d',
          bgBottom: '#1a2f1a',
        };
      case 'excluded':
        return {
          border: '#c62828',
          innerBorder: '#ef5350',
          bgTop: '#4a2020',
          bgBottom: '#2a1010',
        };
      case 'locked':
      default:
        return {
          border: '#616161',
          innerBorder: '#757575',
          bgTop: '#3a3a3a',
          bgBottom: '#1a1a1a',
        };
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.options.width = width;
    this.options.height = height;
  }

  dispose(): void {
    cancelAnimationFrame(this.animationFrame);
    this.canvas.remove();
  }
}
